import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight,
  DollarSign, Wallet, Landmark, Clock, CheckCircle,
  XCircle, PieChart, BarChart3, Loader2, FileText, X,
  GitBranch, ChevronUp, ArrowUp, ArrowDown
} from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import useApi from '../../../utils/useApi';
import useApiGetPost from '../../../hooks/useApiGetPost';
import dayjs from 'dayjs';


const PieChartComponent = ({ data, size = 120 }) => {
  const total = data.reduce((sum, item) => sum + (item.value || 0), 0);
  let currentAngle = -90;

  const getColor = (index) => {
    const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];
    return colors[index % colors.length];
  };

  return (
    <div className="flex items-center gap-4">
      <svg width={size} height={size} viewBox="0 0 100 100">
        {total === 0 ? (
          <circle cx="50" cy="50" r="40" fill="#e2e8f0" />
        ) : (
          data.map((item, index) => {
            const val = item.value || 0;
            if (val === 0) return null;
            const percentage = (val / total) * 100;
            const angle = (percentage / 100) * 360;
            const largeArc = angle > 180 ? 1 : 0;

            const startAngleRad = (currentAngle * Math.PI) / 180;
            const endAngleRad = ((currentAngle + angle) * Math.PI) / 180;

            const x1 = 50 + 40 * Math.cos(startAngleRad);
            const y1 = 50 + 40 * Math.sin(startAngleRad);
            const x2 = 50 + 40 * Math.cos(endAngleRad);
            const y2 = 50 + 40 * Math.sin(endAngleRad);

            const path = `M 50 50 L ${x1} ${y1} A 40 40 0 ${largeArc} 1 ${x2} ${y2} Z`;

            currentAngle += angle;

            return (
              <path
                key={index}
                d={path}
                fill={getColor(index)}
                className="transition-all duration-300 hover:opacity-80"
              />
            );
          })
        )}
      </svg>
      <div className="space-y-2">
        {data.map((item, index) => (
          <div key={index} className="flex items-center gap-2">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: getColor(index) }} />
            <span className="text-[10px] text-slate-600">{item.label}</span>
            <span className="text-[10px] font-bold text-slate-800">{(item.value || 0).toLocaleString()}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const AnimatedCounter = ({ value, duration = 1200 }) => {
  const [display, setDisplay] = useState('0');
  useEffect(() => {
    const numericVal = parseFloat(value.replace(/[^0-9.-]/g, ''));
    if (isNaN(numericVal)) { setDisplay(value); return; }
    let start = 0;
    const step = Math.ceil(numericVal / (duration / 16));
    const timer = setInterval(() => {
      start += step;
      if (start >= numericVal) { setDisplay(value); clearInterval(timer); return; }
      const formatted = numericVal >= 1000 ? start.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : start.toFixed(2);
      setDisplay(value.replace(/[0-9,.-]+/, formatted));
    }, 16);
    return () => clearInterval(timer);
  }, [value, duration]);
  return <span>{display}</span>;
};



const FadeInCard = ({ children, delay = 0, className = '' }) => (
  <div
    className={`animate-fadeInUp ${className}`}
    style={{ animationDelay: `${delay}ms`, animationFillMode: 'both' }}
  >
    {children}
  </div>
);

export default function FinanceDashboard() {
  const [timeframe, setTimeframe] = useState('Today');
  const [selectedBranch, setSelectedBranch] = useState(localStorage.getItem("currentSelectedDepartment")?.trim());
  const [isBranchDropdownOpen, setIsBranchDropdownOpen] = useState(false);

  // const [fromDate, setFromDate] = useState('2024-01-01');
  // const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);

  const today = new Date().toISOString().split("T")[0];

  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);

  const { data: expenseData, loading: expenseLoading, get: fetchExpenseData } = useApi();
  const { data: expiryCountData, loading: expiryCountLoading, get: fetchExpiryCount } = useApi();
  const { data: expiryListData, loading: expiryListLoading, get: fetchExpiryList } = useApi();
  const { data: collectionData, loading: collectionLoading, get: fetchCollectionData } = useApi();
  const { data: receivablesData, loading: receivablesLoading, get: fetchReceivablesData } = useApi();
  const { data: cashBranchData, loading: cashBranchLoading, get: fetchCashBranchData } = useApi();
  const { data: cashAccountData, loading: cashAccountLoading, get: fetchCashAccountData } = useApi();
  const { data: bankTotalData, loading: bankTotalLoading, get: fetchBankTotalData } = useApi();
  const { data: bankAccountData, loading: bankAccountLoading, get: fetchBankAccountData } = useApi();
  const { data: pdcReceivedData, loading: pdcReceivedLoading, get: fetchPdcReceivedData } = useApi();
  const { data: pdcIssuedData, loading: pdcIssuedLoading, get: fetchPdcIssuedData } = useApi();
  const { data: salesData, loading: salesLoading, get: fetchSalesData } = useApi();
  const { data: ageingData, loading: ageingLoading, get: fetchAgeingData } = useApi();
  const [showExpiryModal, setShowExpiryModal] = useState(false);
  const [showCollectionModal, setShowCollectionModal] = useState(false);
  const [showReceivablesModal, setShowReceivablesModal] = useState(false);
  const [showCashModal, setShowCashModal] = useState(false);
  const [showBankModal, setShowBankModal] = useState(false);
  const [showPdcReceivedModal, setShowPdcReceivedModal] = useState(false);
  const [showPdcIssuedModal, setShowPdcIssuedModal] = useState(false);
  const [pdcIssuedSortOrder, setPdcIssuedSortOrder] = useState(null);
  const [pdcReceivedSortOrder, setPdcReceivedSortOrder] = useState(null);
  const [cashSortOrder, setCashSortOrder] = useState(null);
  const [bankSortOrder, setBankSortOrder] = useState(null);
  const [receivablesSortOrder, setReceivablesSortOrder] = useState(null);
  const { data: pdcReceivedListData, loading: pdcReceivedListLoading, get: fetchPdcReceivedList } = useApi();
  const { data: pdcIssuedListData, loading: pdcIssuedListLoading, get: fetchPdcIssuedList } = useApi();


  const dropdownRef = useRef(null);
  const branchPillRef = useRef(null);
  const portalDropdownRef = useRef(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });

  const updateDropdownPosition = () => {
    if (branchPillRef.current) {
      const rect = branchPillRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 8,
        left: rect.left + rect.width / 2,
      });
    }
  };


  // CLOSE BRANCH DROPDOWN ON CLICK OUTSIDE
  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedDropdown = dropdownRef.current?.contains(event.target) || portalDropdownRef.current?.contains(event.target);
      if (!clickedDropdown) {
        setIsBranchDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // UPDATE DROPDOWN POSITION ON SCROLL/RESIZE
  useEffect(() => {
    if (!isBranchDropdownOpen) return;
    const handleReposition = () => updateDropdownPosition();
    window.addEventListener("scroll", handleReposition, true);
    window.addEventListener("resize", handleReposition);
    return () => {
      window.removeEventListener("scroll", handleReposition, true);
      window.removeEventListener("resize", handleReposition);
    };
  }, [isBranchDropdownOpen]);

  const {
    loading: loadingBranchListOfThisUser,
    data: apiBranchListOfThisUser,
    get: getBranchListOfThisUser,
  } = useApiGetPost();


  const branchOptionsWithAll = React.useMemo(() => {
    console.log("apiBranchListOfThisUser ", apiBranchListOfThisUser);
    if (!apiBranchListOfThisUser) return [];
    return [...apiBranchListOfThisUser];
  }, [apiBranchListOfThisUser]);

  useEffect(() => {
    if (apiBranchListOfThisUser) {
      if (apiBranchListOfThisUser?.length > 0) {
        callApis();
      }
    }
  }, [apiBranchListOfThisUser, selectedBranch]);





  let apiPaths_from_local_storage = localStorage.getItem("apiPaths")
    ? JSON.parse(localStorage.getItem("apiPaths"))
    : "-";
  let baseUrl = apiPaths_from_local_storage[0].API_CONFIG;
  // console.log("🚀 ~ FinanceDashboard ~ baseUrl:", baseUrl)

  let company_code_from_local_storage = localStorage.getItem("companyCode")
    ? localStorage.getItem("companyCode")
    : "-";
  // console.log("🚀 ~ FinanceDashboard ~ company_code_from_local_storage:", company_code_from_local_storage)


  useEffect(() => {
    callApis()
  }, []);


  useEffect(() => {
    const currentUserName = localStorage.getItem("currentUserName");
    let trimmedUsername = currentUserName?.trim();
    let urlBranchOfUser = `${baseUrl}/api/MasterCount/${company_code_from_local_storage}/BRANCH/${trimmedUsername}/-`;
    console.log("urlBranchOfUser ", urlBranchOfUser);
    getBranchListOfThisUser(urlBranchOfUser);
  }, []);

  // const getBranchListOfThisUser=()=>{

  // }



  const branchParam = selectedBranch === "All" ? "-" : selectedBranch;
  const callApis = () => {
    // fetchExpiryCount(`${baseUrl}/api/CpaysCount/SUNRISEDXB/ACCDOCE XPIRYCNT/-/-`);
    fetchExpiryCount(`${baseUrl}/api/CpaysCount/${company_code_from_local_storage}/ACCDOCEXPIRYCNT/-/-`);
    // fetchExpiryList(`${baseUrl}/api/CpaysCount/SUNRISEDXB/ACCDOCEXPIRYLIST/-/-`);
    fetchExpiryList(`${baseUrl}/api/CpaysCount/${company_code_from_local_storage}/ACCDOCEXPIRYLIST/-/-`);
    fetchCollectionData(`${baseUrl}/api/HRDashboard/${company_code_from_local_storage}/${branchParam}/Collection_kpi/${fromDate}/${toDate}/-/-/-/-/-`);
    fetchReceivablesData(`${baseUrl}/api/HRDashboard/${company_code_from_local_storage}/${branchParam}/Receivables_Sum/${fromDate}/${toDate}/-/-/-/-/-`);
    fetchPdcReceivedData(`${baseUrl}/api/HRDashboard/${company_code_from_local_storage}/${branchParam}/PDCPENDSUMM/${fromDate}/${toDate}/R/-/-/-/-`);
    fetchPdcIssuedData(`${baseUrl}/api/HRDashboard/${company_code_from_local_storage}/${branchParam}/PDCPENDSUMM/${fromDate}/${toDate}/I/-/-/-/-`);
    fetchSalesData(`${baseUrl}/api/HRDashboard/${company_code_from_local_storage}/${branchParam}/MONTH_SALES_COMP/${fromDate}/${toDate}/-/-/-/-/-`);
    fetchAgeingData(`${baseUrl}/api/HRDashboard/${company_code_from_local_storage}/${branchParam}/AGEINGREPORT/${fromDate}/${toDate}/-/-/-/-/-`);
      fetchExpenseData(`${baseUrl}/api/HRDashboard/${company_code_from_local_storage}/${branchParam}/Monthly_Expense_Summary/${fromDate}/${toDate}/-/-/-/-/-`);

    fetchCashBranchData(`${baseUrl}/api/HRDashboard/${company_code_from_local_storage}/-/Cash_Branchwise_AsOn/${fromDate}/${toDate}/-/-/-/-/-`);
    fetchBankTotalData(`${baseUrl}/api/HRDashboard/${company_code_from_local_storage}/-/Bank_Total_AsOn/${fromDate}/${toDate}/-/-/-/-/-`);
  }

  useEffect(() => {
    if (showExpiryModal) {
      // fetchExpiryList(`${baseUrl}/api/CpaysCount/SUNRISEDXB/ACCDOCEXPIRYLIST/-/-`);
      fetchExpiryList(`${baseUrl}/api/CpaysCount/${company_code_from_local_storage}/ACCDOCEXPIRYLIST/-/-`);
    }
  }, [showExpiryModal]);

  useEffect(() => {
    if (showCashModal) {
      fetchCashAccountData(`${baseUrl}/api/HRDashboard/${company_code_from_local_storage}/-/Cash_Accountwise_AsOn/${fromDate}/${toDate}/-/-/-/-/-`);
    }
  }, [showCashModal]);

  useEffect(() => {
    if (showBankModal) {
      fetchBankAccountData(`${baseUrl}/api/HRDashboard/${company_code_from_local_storage}/-/Bank_Accountwise_AsOn/${fromDate}/${toDate}/-/-/-/-/-`);
    }
  }, [showBankModal]);

  useEffect(() => {
    if (showReceivablesModal) {
      // fetchAgeingData(`${baseUrl}/api/HRDashboard/SUNRISEDXB/-/AGEINGREPORT/2024-01-01/2024-01-01/-/-/-/-/-`);
      fetchAgeingData(`${baseUrl}/api/HRDashboard/${company_code_from_local_storage}/${branchParam}/AGEINGREPORT/${fromDate}/${toDate}/-/-/-/-/-`);
    }
  }, [showReceivablesModal]);

  useEffect(() => {
    if (showPdcReceivedModal) {
      fetchPdcReceivedList(`${baseUrl}/api/HRDashboard/${company_code_from_local_storage}/${branchParam}/PDCPENDLIST/${fromDate}/${toDate}/R/-/-/-/-`);
    }
  }, [showPdcReceivedModal]);

  useEffect(() => {
    if (showPdcIssuedModal) {
      fetchPdcIssuedList(`${baseUrl}/api/HRDashboard/${company_code_from_local_storage}/${branchParam}/PDCPENDLIST/${fromDate}/${toDate}/I/-/-/-/-`);
    }
  }, [showPdcIssuedModal]);

  const sortedPdcIssuedListData = useMemo(() => {
    if (!pdcIssuedListData || !Array.isArray(pdcIssuedListData)) return [];
    if (!pdcIssuedSortOrder) return pdcIssuedListData;
    const sorted = [...pdcIssuedListData].sort((a, b) => (a.AMOUNT || 0) - (b.AMOUNT || 0));
    return pdcIssuedSortOrder === 'desc' ? sorted.reverse() : sorted;
  }, [pdcIssuedListData, pdcIssuedSortOrder]);

  const sortedPdcReceivedListData = useMemo(() => {
    if (!pdcReceivedListData || !Array.isArray(pdcReceivedListData)) return [];
    if (!pdcReceivedSortOrder) return pdcReceivedListData;
    const sorted = [...pdcReceivedListData].sort((a, b) => (a.AMOUNT || 0) - (b.AMOUNT || 0));
    return pdcReceivedSortOrder === 'desc' ? sorted.reverse() : sorted;
  }, [pdcReceivedListData, pdcReceivedSortOrder]);

  const sortedCashAccountData = useMemo(() => {
    if (!cashAccountData || !Array.isArray(cashAccountData)) return [];
    if (!cashSortOrder) return cashAccountData;
    const sorted = [...cashAccountData].sort((a, b) => (a.CashBalance || 0) - (b.CashBalance || 0));
    return cashSortOrder === 'desc' ? sorted.reverse() : sorted;
  }, [cashAccountData, cashSortOrder]);

  const sortedBankAccountData = useMemo(() => {
    if (!bankAccountData || !Array.isArray(bankAccountData)) return [];
    if (!bankSortOrder) return bankAccountData;
    const sorted = [...bankAccountData].sort((a, b) => (a.BankBalance || 0) - (b.BankBalance || 0));
    return bankSortOrder === 'desc' ? sorted.reverse() : sorted;
  }, [bankAccountData, bankSortOrder]);

  const sortedAgeingData = useMemo(() => {
    if (!ageingData || !Array.isArray(ageingData)) return [];
    if (!receivablesSortOrder) return ageingData;
    const sorted = [...ageingData].sort((a, b) => (a.Total_Amount || 0) - (b.Total_Amount || 0));
    return receivablesSortOrder === 'desc' ? sorted.reverse() : sorted;
  }, [ageingData, receivablesSortOrder]);

  useEffect(() => {
    if (selectedBranch && fromDate && toDate) {
      // fetchExpenseData(`${baseUrl}/api/HRDashboard/shinka/${selectedBranch}/Monthly_Expense_Summary/${fromDate}/${toDate}/-/-/-/-/-`);
      fetchExpenseData(`${baseUrl}/api/HRDashboard/${company_code_from_local_storage}/${branchParam}/Monthly_Expense_Summary/${fromDate}/${toDate}/-/-/-/-/-`);
    }
  }, [selectedBranch, fromDate, toDate]);

  const expenseChartData = useMemo(() => {
    if (!expenseData || !Array.isArray(expenseData) || expenseData.length === 0) {
      return [];
    }

    const mappedData = expenseData
      .filter(item => item.Month && item.TotalExpense !== undefined)
      .map(item => {
        const [year, month] = item.Month.split('-');
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const monthIndex = parseInt(month, 10) - 1;

        return {
          month: `${monthNames[monthIndex]} ${year.slice(2)}`,
          value: Math.round(parseFloat(item.TotalExpense) || 0),
          rawMonth: item.Month,
          entries: item.NoOfEntries || 0
        };
      })
      .sort((a, b) => a.rawMonth.localeCompare(b.rawMonth));

    return mappedData;
  }, [expenseData]);

  const collectionMetrics = useMemo(() => {
    if (!collectionData || !Array.isArray(collectionData) || collectionData.length === 0) {
      return null;
    }
    return collectionData[0];
  }, [collectionData]);

  const receivablesMetrics = useMemo(() => {
    if (!receivablesData || !Array.isArray(receivablesData) || receivablesData.length === 0) {
      return null;
    }
    return receivablesData[0];
  }, [receivablesData]);

  const cashBranchMetrics = useMemo(() => {
    if (!cashBranchData || !Array.isArray(cashBranchData) || cashBranchData.length === 0) {
      return null;
    }
    return cashBranchData[0];
  }, [cashBranchData]);

  const bankTotalMetrics = useMemo(() => {
    if (!bankTotalData || !Array.isArray(bankTotalData) || bankTotalData.length === 0) {
      return null;
    }
    return bankTotalData[0];
  }, [bankTotalData]);

  const pdcReceivedMetrics = useMemo(() => {
    if (!pdcReceivedData || !Array.isArray(pdcReceivedData) || pdcReceivedData.length === 0) {
      return null;
    }
    return pdcReceivedData[0];
  }, [pdcReceivedData]);

  const pdcIssuedMetrics = useMemo(() => {
    if (!pdcIssuedData || !Array.isArray(pdcIssuedData) || pdcIssuedData.length === 0) {
      return null;
    }
    return pdcIssuedData[0];
  }, [pdcIssuedData]);

  const CHART_COLOR_PRIMARY = '#10b981';
  const CHART_COLOR_SECONDARY = '#6366f1';

  const salesMetrics = useMemo(() => {
    if (!salesData || !Array.isArray(salesData) || salesData.length === 0) {
      return null;
    }
    const sorted = [...salesData].sort((a, b) => a.SaleMonth.localeCompare(b.SaleMonth));
    const latest = sorted[sorted.length - 1];
    return {
      totalSales: latest?.TotalSales,
      growthPercent: latest?.GrowthPercent,
      salesCount: latest?.SalesCount,
      avgSaleAmount: latest?.AvgSaleAmount,
    };
  }, [salesData]);

  const revenueTrend = useMemo(() => {
    if (!salesData || !Array.isArray(salesData) || salesData.length === 0) {
      return [];
    }
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const sorted = [...salesData].sort((a, b) => a.SaleMonth.localeCompare(b.SaleMonth));
    return sorted.map(item => {
      const [year, month] = item.SaleMonth.split('-');
      return {
        m: `${monthNames[parseInt(month, 10) - 1]} '${year.slice(2)}`,
        revenue: item.TotalSales,
      };
    });
  }, [salesData]);

  const avgSaleTrend = useMemo(() => {
    if (!salesData || !Array.isArray(salesData) || salesData.length === 0) {
      return [];
    }
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const sorted = [...salesData].sort((a, b) => a.SaleMonth.localeCompare(b.SaleMonth));
    return sorted.map(item => {
      const [year, month] = item.SaleMonth.split('-');
      return {
        m: `${monthNames[parseInt(month, 10) - 1]} '${year.slice(2)}`,
        avgSale: item.AvgSaleAmount,
      };
    });
  }, [salesData]);




  // TOPLINE Metrics - Finance Focused
  const toplineMetrics = [
    {
      label: 'COLLECTION (MTD)',
      value: collectionLoading ? 'Loading...' : (collectionMetrics?.MonthTotalCollection?.toLocaleString() ?? '0'),
      subLabel: `Today: ${collectionMetrics?.TodayTotalCollection?.toLocaleString() ?? '0'}`,
      change: 'Click for breakdown',
      positive: true,
      icon: DollarSign,
      gradient: 'from-blue-400 to-indigo-500',
      onClick: () => setShowCollectionModal(true)
    },
    {
      label: 'RECEIVABLES',
      value: receivablesLoading ? 'Loading...' : (receivablesMetrics?.TotalReceivables?.toLocaleString() ?? '0'),
      subLabel: 'Outstanding amount',
      change: 'Click for details',
      positive: false,
      icon: Clock,
      gradient: 'from-amber-400 to-orange-500',
      onClick: () => setShowReceivablesModal(true)
    },
    {
      label: 'CASH BALANCE',
      value: cashBranchLoading ? 'Loading...' : (cashBranchMetrics?.CashBalance?.toLocaleString() ?? '0'),
      subLabel: 'Branch cash balance',
      change: 'Click for details',
      positive: true,
      icon: Wallet,
      gradient: 'from-emerald-400 to-green-500',
      onClick: () => setShowCashModal(true)
    },
    {
      label: 'BANK BALANCE',
      value: bankTotalLoading ? 'Loading...' : (bankTotalMetrics?.TotalBankBalance?.toLocaleString() ?? '0'),
      subLabel: 'Total bank balance',
      change: 'Click for details',
      positive: true,
      icon: Landmark,
      gradient: 'from-cyan-400 to-blue-500',
      onClick: () => setShowBankModal(true)
    },
    {
      label: 'PDC RECEIVED',
      value: pdcReceivedLoading ? 'Loading...' : (pdcReceivedMetrics?.amt?.toLocaleString() ?? '0'),
      subLabel: 'Post-dated cheques received',
      change: 'Click for details',
      positive: true,
      icon: CheckCircle,
      gradient: 'from-indigo-400 to-violet-500',
      onClick: () => setShowPdcReceivedModal(true)
    },
    {
      label: 'PDC ISSUED',
      value: pdcIssuedLoading ? 'Loading...' : (pdcIssuedMetrics?.amt?.toLocaleString() ?? '0'),
      subLabel: 'Post-dated cheques issued',
      change: 'Click for details',
      positive: false,
      icon: XCircle,
      gradient: 'from-rose-400 to-pink-500',
      onClick: () => setShowPdcIssuedModal(true)
    },
  ];

  // Financial Metrics
  const financialMetrics = {
    currentMonthRevenue: salesMetrics?.totalSales ? salesMetrics.totalSales.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0',
    avgSale: salesMetrics?.avgSaleAmount ? salesMetrics.avgSaleAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0',
    growthPercent: salesMetrics?.growthPercent != null ? `${salesMetrics.growthPercent}%` : '0%',
    achievementPercent: '78%',
    outstandingReceivables: '64,120.00',
    tenantsInArrears: '14'
  };




  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 font-sans antialiased flex">
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(24px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulse-soft {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
        @keyframes gradient-shift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        .animate-fadeInUp {
          animation: fadeInUp 0.6s ease-out forwards;
        }
        .animate-pulse-soft {
          animation: pulse-soft 2s ease-in-out infinite;
        }
        .gradient-animate {
          background-size: 200% 200%;
          animation: gradient-shift 4s ease infinite;
        }
        .card-hover-lift {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .card-hover-lift:hover {
          transform: translateY(-4px);
          box-shadow: 0 20px 40px -12px rgba(0, 0, 0, 0.12);
        }
        .metric-icon-pulse {
          transition: all 0.3s ease;
        }
        .card-hover-lift:hover .metric-icon-pulse {
          transform: scale(1.15) rotate(-3deg);
        }
        .branch-btn {
          transition: all 0.2s ease;
          position: relative;
        }
        .branch-btn.active {
          background: linear-gradient(135deg, #6366f1, #8b5cf6);
          color: white;
          transform: scale(1.05);
          box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
        }
        .branch-btn.active .branch-dot {
          background: white !important;
        }
        .time-btn {
          transition: all 0.2s ease;
        }
        .time-btn.active {
          background: linear-gradient(135deg, #6366f1, #8b5cf6);
          color: white;
          box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
        }
        .chart-bar {
          transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .chart-bar:hover {
          transform: scaleY(1.1) translateY(-2px);
          opacity: 0.9;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>

      <div className="flex-1 flex flex-col min-w-0">
        <main className="flex-1 p-6 lg:p-8 space-y-6 overflow-y-auto">

          {/* Header */}
          <FadeInCard delay={0}>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h1 className="text-2xl font-extrabold tracking-tight bg-gradient-to-r from-slate-900 via-slate-700 to-indigo-800 bg-clip-text text-transparent">
                  <span className="text-2xl font-medium text-slate-900">Financial Dashboard</span>
                </h1>
                <p className="text-xs text-slate-500 mt-1">Real-time financial performance tracking</p>
              </div>

              <div ref={branchPillRef} className="relative" style={{ width: "120px" }}>
                <div ref={dropdownRef} style={{ width: "100%", position: "relative" }}>
                  {createPortal(
                    isBranchDropdownOpen && (
                      <div
                        ref={portalDropdownRef}
                        style={{
                          position: "fixed",
                          top: dropdownPos.top,
                          left: dropdownPos.left,
                          transform: "translateX(-50%)",
                          width: "180px",
                          background: "#ffffff",
                          borderRadius: "14px",
                          padding: "6px",
                          boxShadow: "0 10px 25px -5px rgba(15, 23, 42, 0.25)",
                          border: "1px solid #e2e8f0",
                          display: "flex",
                          flexDirection: "column",
                          gap: "4px",
                          maxHeight: "280px",
                          overflowY: "auto",
                          zIndex: 99999,
                        }}
                      >
                        {branchOptionsWithAll?.map((branch) => {
                          const isSelected = selectedBranch === branch.DEPTNO?.trim();
                          return (
                            <button
                              key={branch.DEPTNO}
                              onClick={() => {
                                setSelectedBranch(branch.DEPTNO?.trim());
                                setIsBranchDropdownOpen(false);
                              }}
                              style={{
                                width: "100%",
                                padding: "10px 12px",
                                borderRadius: "8px",
                                border: "none",
                                background: isSelected
                                  ? `${CHART_COLOR_PRIMARY}12`
                                  : "transparent",
                                color: isSelected ? CHART_COLOR_PRIMARY : "#334155",
                                fontWeight: isSelected ? 600 : 500,
                                fontSize: "12px",
                                textAlign: "left",
                                cursor: "pointer",
                                display: "flex",
                                flexDirection: "column",
                                gap: "2px",
                              }}
                            >
                              <span style={{ fontSize: "11px", lineHeight: "1.2" }}>{branch.deptnodesc}</span>
                              <span style={{ fontSize: "9px", opacity: 0.6 }}>{branch.DEPTNO}</span>
                            </button>
                          );
                        })}
                      </div>
                    ),
                    document.body
                  )}

                  <button
                    onClick={() => {
                      if (!isBranchDropdownOpen) updateDropdownPosition();
                      setIsBranchDropdownOpen(!isBranchDropdownOpen);
                    }}
                    style={{
                      width: "100%",
                      background: CHART_COLOR_PRIMARY,
                      color: "#ffffff",
                      border: "none",
                      borderRadius: "999px",
                      padding: "10px 18px",
                      fontSize: "12px",
                      fontWeight: 600,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                      // boxShadow: "0 4px 14px rgba(99, 102, 241, 0.35)",
                      cursor: "pointer",
                    }}
                  >
                    <GitBranch size={14} />
                    <span
                      style={{
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {branchOptionsWithAll?.find(b => b.DEPTNO === selectedBranch)?.deptnodesc || selectedBranch}
                    </span>
                    <ChevronUp
                      size={14}
                      style={{
                        transform: isBranchDropdownOpen
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                        transition: "transform 0.2s ease",
                        marginLeft: "auto",
                      }}
                    />
                  </button>
                </div>
              </div>
            </div>
          </FadeInCard>

          {/* TOPLINE Metrics Grid */}
          <div className="grid grid-cols-6 gap-3">
            {toplineMetrics.map((metric, idx) => {
              const Icon = metric.icon;
              return (
                <FadeInCard key={idx} delay={50 + idx * 30}>
                  <div
                    onClick={metric.onClick}
                    className={`bg-white border border-slate-200/80 rounded-xl p-3 shadow-sm card-hover-lift overflow-hidden relative group ${metric.onClick ? 'cursor-pointer' : ''}`}
                  >
                    <div className={`absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r ${metric.gradient}`} />
                    <div className="relative z-10">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="text-[8px] font-bold text-slate-400 uppercase tracking-wider truncate">{metric.label}</p>
                          <p className="text-sm font-extrabold text-slate-900 truncate mt-0.5">
                            <AnimatedCounter value={metric.value} duration={800} />
                          </p>
                          {metric.subLabel && (
                            <p className="text-[9px] font-semibold text-slate-600 truncate">{metric.subLabel}</p>
                          )}
                          {metric.change && (
                            <p className={`text-[8px] font-bold ${metric.positive ? 'text-emerald-600' : 'text-rose-600'} truncate flex items-center gap-0.5`}>
                              {metric.positive ? <ArrowUpRight size={10} /> : <ArrowDownRight size={10} />}
                              {metric.change}
                            </p>
                          )}
                        </div>
                        <div className={`p-1.5 bg-gradient-to-br ${metric.gradient} rounded-lg shrink-0 ml-1`}>
                          <Icon size={12} className="text-white" />
                        </div>
                      </div>
                    </div>
                  </div>
                </FadeInCard>
              );
            })}


          </div>

          {/* Financial Performance Section */}
          {/* Financial Performance Section — same fix: responsive + equal-height */}
          <div className="grid grid-cols-1 sm:grid-cols-2  gap-4">
            <FadeInCard delay={350}>
              <div className="h-full min-h-[180px] flex flex-col bg-white border border-slate-200/80 rounded-xl p-3 shadow-sm card-hover-lift overflow-hidden relative group">
                <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-emerald-400 to-green-500" />
                <div className="relative z-10 flex-1 flex flex-col">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider truncate">REVENUE</p>
                      <p className="text-base font-extrabold text-slate-900 truncate mt-0.5">
                        {salesLoading ? (
                          <Loader2 size={15} className="animate-spin text-emerald-500" />
                        ) : (
                          <AnimatedCounter value={financialMetrics.currentMonthRevenue} />
                        )}
                      </p>
                    </div>
                    <div className="p-1.5 bg-gradient-to-br from-emerald-400 to-green-500 rounded-lg shrink-0 ml-1">
                      <DollarSign size={13} className="text-white" />
                    </div>
                  </div>
                  <div className="mt-auto pt-1.5">
                    <p className="text-[10px] font-semibold text-slate-600 truncate">this month</p>
                    <p className={`text-[9px] font-bold flex items-center gap-0.5 ${salesMetrics?.growthPercent >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                      {salesMetrics?.growthPercent >= 0 ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
                      {financialMetrics.growthPercent} vs last period
                    </p>
                  </div>
                  {/* Area Chart */}
                  <div className="mt-2 -mx-1">
                    {salesLoading ? (
                      <div className="h-[70px] flex items-center justify-center">
                        <Loader2 size={16} className="animate-spin text-emerald-400" />
                      </div>
                    ) : revenueTrend.length > 0 ? (
                      <ResponsiveContainer width="100%" height={70}>
                        <AreaChart data={revenueTrend} margin={{ top: 4, right: 4, left: -10, bottom: 0 }}>
                          <defs>
                            <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="10%" stopColor={CHART_COLOR_PRIMARY} stopOpacity={0.2} />
                              <stop offset="95%" stopColor={CHART_COLOR_PRIMARY} stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <XAxis dataKey="m" tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                          <YAxis tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                          <Tooltip
                            formatter={(v) => [`${v.toLocaleString()}`, 'Revenue']}
                            contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #e2e8f0' }}
                          />
                          <Area type="monotone" dataKey="revenue" stroke={CHART_COLOR_PRIMARY} strokeWidth={2} fill="url(#revGrad)" dot={true} />
                        </AreaChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-[70px] flex items-center justify-center text-[9px] text-slate-400">No data</div>
                    )}
                  </div>
                </div>
              </div>
            </FadeInCard>

            <FadeInCard delay={430}>
              <div className="h-full min-h-[180px] flex flex-col bg-white border border-slate-200/80 rounded-xl p-3 shadow-sm card-hover-lift overflow-hidden relative group">
                <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-indigo-400 to-violet-500" />
                <div className="relative z-10 flex-1 flex flex-col">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider truncate">GROSS MARGIN & PROFIT</p>
                      <p className="text-base font-extrabold text-slate-900 truncate mt-0.5">
                        {salesLoading ? (
                          <Loader2 size={15} className="animate-spin text-indigo-500" />
                        ) : (
                          <AnimatedCounter value={financialMetrics.avgSale} />
                        )}
                      </p>
                    </div>
                    <div className="p-1.5 bg-gradient-to-br from-indigo-400 to-violet-500 rounded-lg shrink-0 ml-1">
                      <PieChart size={13} className="text-white" />
                    </div>
                  </div>
                  <div className="mt-auto pt-1.5">
                    <p className="text-[10px] font-semibold text-slate-600 truncate">Avg Sale</p>
                    <p className={`text-[9px] font-bold flex items-center gap-0.5 ${salesMetrics?.growthPercent >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                      {salesMetrics?.growthPercent >= 0 ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
                      {financialMetrics.growthPercent} growth rate
                    </p>
                  </div>
                  {/* Area Chart */}
                  <div className="mt-2 -mx-1">
                    {salesLoading ? (
                      <div className="h-[70px] flex items-center justify-center">
                        <Loader2 size={16} className="animate-spin text-indigo-400" />
                      </div>
                    ) : avgSaleTrend.length > 0 ? (
                      <ResponsiveContainer width="100%" height={70}>
                        <AreaChart data={avgSaleTrend} margin={{ top: 4, right: 4, left: -10, bottom: 0 }}>
                          <defs>
                            <linearGradient id="avgSaleGrad" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="10%" stopColor={CHART_COLOR_SECONDARY} stopOpacity={0.2} />
                              <stop offset="95%" stopColor={CHART_COLOR_SECONDARY} stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <XAxis dataKey="m" tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                          <YAxis tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                          <Tooltip
                            formatter={(v) => [`${v.toLocaleString()}`, 'Avg Sale']}
                            contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #e2e8f0' }}
                          />
                          <Area type="monotone" dataKey="avgSale" stroke={CHART_COLOR_SECONDARY} strokeWidth={2} fill="url(#avgSaleGrad)" dot={true} />
                        </AreaChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-[70px] flex items-center justify-center text-[9px] text-slate-400">No data</div>
                    )}
                  </div>
                </div>
              </div>
            </FadeInCard>


          </div>

          {/* Revenue Trend Chart Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2  gap-4">
            {/* <FadeInCard delay={670}>
              <div className="bg-white border border-slate-200/80 rounded-2xl p-5 shadow-sm">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                  <div>
                    <h3 className="text-sm font-bold text-slate-900 uppercase tracking-widest flex items-center gap-2">
                      <span className="w-1.5 h-5 bg-gradient-to-b from-indigo-500 to-purple-600 rounded-full" />
                      REVENUE TREND
                    </h3>
                    <p className="text-xs text-slate-400 mt-0.5">Monthly revenue performance with target comparison</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded bg-indigo-500"></span>
                      <span className="text-xs text-slate-600">Revenue</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded bg-emerald-500"></span>
                      <span className="text-xs text-slate-600">Target</span>
                    </div>
                  </div>
                </div>

                <div className="h-64">
                  <div className="flex items-end gap-1.5 h-full pt-2">
                    {revenueTrend.map((item, i) => {
                      const maxRevenue = Math.max(...revenueTrend.map(d => d.revenue));
                      const maxTarget = Math.max(...targetVsActual.map(d => d.Target));
                      const maxVal = Math.max(maxRevenue, maxTarget);
                      
                      return (
                        <div key={i} className="flex-1 flex flex-col items-center gap-1 group">
                          <div className="flex items-end gap-1 h-[180px] w-full relative">
                            <div 
                              className="w-3/5 bg-gradient-to-t from-indigo-500 to-indigo-400 rounded-t-sm transition-all duration-500 hover:opacity-80"
                              style={{ height: `${(item.revenue / maxVal) * 100}%` }}
                            />
                            <div 
                              className="w-3/5 bg-gradient-to-t from-emerald-500 to-emerald-400 rounded-t-sm transition-all duration-500 hover:opacity-80"
                              style={{ height: `${(targetVsActual[i]?.Target / maxVal) * 100}%` }}
                            />
                          </div>
                          <span className="text-[10px] font-medium text-slate-500">{item.m}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </FadeInCard> */}

            {/* Monthly Expense Analysis Bar Chart - FIXED */}
            <FadeInCard delay={700}>
              {/* <div className="bg-white border border-slate-200/80 rounded-2xl p-5 shadow-sm "> */}
              <div className="relative overflow-hidden bg-white border border-slate-200/80 rounded-2xl p-5 shadow-sm card-hover-lift">
                {/* Gradient Top Border */}
                <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-amber-400 via-orange-500 to-red-500" />

                <div className="relative z-10">

                  <div className='flex items-center justify-between '>
                    <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider truncate"> MONTHLY EXPENSE ANALYSIS</p>
                    <div className="p-1.5 bg-gradient-to-br from-amber-400 to-red-500 rounded-lg shrink-0 ml-1">
                      <BarChart3 size={13} className="text-white" />
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                    <div className=''>
                      {/* Summary stats */}
                      {expenseChartData.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-slate-100 flex justify-between text-xs text-slate-500">
                          <span>Total Months: <strong className="mr-2 text-slate-700">{expenseChartData.length}</strong></span>
                          <span>Total Expense: <strong className="mr-2 text-slate-700">
                            {expenseChartData.reduce((sum, item) => sum + item.value, 0).toLocaleString()}
                          </strong></span>
                          <span>Average: <strong className="text-slate-700">
                            {Math.round(expenseChartData.reduce((sum, item) => sum + item.value, 0) / expenseChartData.length).toLocaleString()}
                          </strong></span>
                        </div>
                      )}


                    </div>

                  </div>

                  <div className="">
                    {expenseLoading ? (
                      <div className="flex items-center justify-center h-full">
                        <Loader2 size={24} className="text-orange-500 animate-spin" />
                        <span className="text-xs text-slate-400 ml-2">Loading expenses...</span>
                      </div>
                    ) : expenseChartData.length > 0 ? (
                      (() => {
                        const values = expenseChartData.map(d => d.value);
                        const maxValue = Math.max(...values);
                        const chartHeight = 220;
                        const yTicks = 5;
                        const tickValues = Array.from({ length: yTicks + 1 }, (_, i) => Math.round((maxValue / yTicks) * i));

                        const getBarColor = (val) => {
                          const ratio = val / maxValue;
                          if (ratio > 0.7) return 'from-red-500 to-rose-400';
                          if (ratio > 0.4) return 'from-orange-500 to-amber-400';
                          return 'from-orange-400 to-yellow-400';
                        };

                        return (
                          <div className="flex  items-center justify-center h-[320px]">
                            {/* Y-Axis */}
                            <div className="flex flex-col justify-between py-2 pr-2 w-16" style={{ height: chartHeight + 24 }}>
                              {tickValues.reverse().map((tick, i) => (
                                <span key={i} className="text-[8px] font-medium text-slate-500 text-right leading-none">
                                  {tick >= 1000 ? `${(tick / 1000).toFixed(0)}K` : tick}
                                </span>
                              ))}
                            </div>

                            {/* Chart Area */}
                            <div className="flex-1 flex flex-col">
                              {/* Grid lines */}
                              <div className="relative" style={{ height: chartHeight }}>
                                {tickValues.slice().reverse().map((tick, i) => (
                                  <div
                                    key={i}
                                    className="absolute w-full border-t border-slate-100"
                                    style={{ bottom: `${(tick / maxValue) * 100}%` }}
                                  />
                                ))}

                                {/* Bars */}
                                <div className="absolute inset-0 flex items-end justify-around gap-2 px-2 pb-1">
                                  {expenseChartData.map((item, i) => {
                                    const barHeightPx = Math.max((item.value / maxValue) * chartHeight, 8);
                                    const formattedValue = item.value.toLocaleString();

                                    return (
                                      <div key={i} className="flex-1 flex flex-col items-center group max-w-[60px] min-w-[28px]">
                                        <div className="text-[8px] font-semibold text-slate-600 mb-1 opacity-0 group-hover:opacity-100 transition-all duration-200">
                                          {formattedValue}
                                        </div>
                                        <div
                                          className={`w-full max-w-[40px] min-w-[16px] bg-gradient-to-t ${getBarColor(item.value)} rounded-t-md transition-all duration-700 ease-out hover:opacity-90 cursor-pointer shadow-sm hover:shadow-md`}
                                          style={{ height: barHeightPx }}
                                        />
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>

                              {/* X-Axis labels */}
                              <div className="flex justify-around gap-2 px-2 pt-2 border-t border-slate-200">
                                {expenseChartData.map((item, i) => (
                                  <span key={i} className="text-[9px] font-medium text-slate-500 whitespace-nowrap flex-1 text-center max-w-[60px]">
                                    {item.month}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>

                        );
                      })()
                    ) : (
                      <div className="flex flex-col items-center justify-center h-[370px] ">
                        <BarChart3 size={32} className="text-slate-300 mb-2" />
                        <span className="text-sm text-slate-400">No expense data available</span>
                        <span className="text-[10px] text-slate-300 mt-1">Select different date range or branch</span>
                      </div>
                    )}
                  </div>

                  <p className="text-[0.6rem] text-slate-400 mt-0.5 ">
                    {expenseChartData.length > 0
                      ? `Showing ${expenseChartData.length} months of expense data`
                      : 'Total expenses aggregated by month'}
                  </p>

                </div>
              </div>
            </FadeInCard>

            <FadeInCard delay={480}>
              <div
                onClick={() => setShowExpiryModal(true)}
                className="h-full max-h-[480px] overflow-y-auto flex flex-col bg-white border border-slate-200/80 rounded-xl p-3 shadow-sm card-hover-lift overflow-hidden relative group cursor-pointer"
              >
                <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-amber-400 to-red-500" />
                <div className="relative z-10 flex-1 flex flex-col">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider truncate">EXPIRING DOCS</p>
                      <p className="text-base font-extrabold text-slate-900 truncate mt-0.5">
                        {expiryCountLoading ? (
                          <Loader2 size={15} className="animate-spin text-amber-500" />
                        ) : (
                          expiryCountData?.[0]?.Cnt ?? 0
                        )}
                        <p className="text-[10px] font-semibold text-slate-600 truncate">Documents</p>
                      </p>
                    </div>
                    <div className="p-1.5 bg-gradient-to-br from-amber-400 to-red-500 rounded-lg shrink-0 ml-1">
                      <FileText size={13} className="text-white" />
                    </div>
                  </div>

                  {/* All Expiry Docs List */}
                  <div className="mt-2 -mx-1 flex-1 overflow-hidden ">
                    {expiryListLoading ? (
                      <div className="h-full flex items-center justify-center">
                        <Loader2 size={16} className="animate-spin text-amber-400" />
                      </div>
                    ) : expiryListData && expiryListData.length > 0 ? (
                      // <div className="space-y-1 h-full overflow-y-auto scrollbar-hide pr-1">
                      //   {expiryListData.map((doc, idx) => (
                      //     <div key={idx} className="flex items-center justify-between bg-amber-50 rounded-lg px-2 py-1.5 hover:bg-amber-100 transition-colors">
                      //       <div className="flex items-center gap-1.5 min-w-0">
                      //         <div className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />
                      //         <div className="flex-1 min-w-0">
                      //           <p className="text-[8px] font-bold text-slate-700 truncate">{doc.AccountName || doc.DocName || doc.VoucherName || 'Document'}</p>
                      //           <p className="text-[7px] text-slate-500 truncate">{doc.ExpiryDate || doc.DocDate || doc.ValidUpto || ''}</p>
                      //         </div>
                      //       </div>
                      //       <span className="text-[7px] font-bold text-amber-600 shrink-0 ml-1">
                      //         {doc.Amount?.toLocaleString() || doc.DocNo || doc.VoucherNo || ''}
                      //       </span>
                      //     </div>
                      //   ))}
                      // </div>
                      <div className="relative bg-white rounded-2xl  w-full  max-h-[80vh] flex flex-col animate-fadeInUp pb-2">


                        <div className="flex-1 overflow-y-auto">
                          {expiryListLoading ? (
                            <div className="flex items-center justify-center py-12">
                              <Loader2 size={24} className="text-amber-500 animate-spin" />
                              <span className="text-xs text-slate-400 ml-2">Loading document list...</span>
                            </div>
                          ) : expiryListData && expiryListData.length > 0 ? (
                            <div className="space-y-2 pb-4">
                              {expiryListData.map((doc, i) => {
                                const expDate = new Date(doc.exp_date);
                                const daysLeft = Math.ceil((expDate - new Date()) / (1000 * 60 * 60 * 24));
                                const isUrgent = daysLeft <= 7;
                                const isExpired = daysLeft < 0;

                                return (
                                  <div
                                    key={i}
                                    className={`flex items-center gap-4 p-3 rounded-xl border transition-all ${isExpired
                                      ? 'bg-gray-50 border-gray-200'
                                      : isUrgent
                                        ? 'bg-amber-50 border-amber-200'
                                        : 'bg-white border-slate-200 hover:border-indigo-200 hover:shadow-sm'
                                      }`}
                                  >
                                    <div className={`p-2 rounded-lg shrink-0 ${isExpired
                                      ? 'bg-red-100'
                                      : isUrgent
                                        ? 'bg-amber-100'
                                        : 'bg-indigo-100'
                                      }`}>
                                      <FileText size={16} className={
                                        isExpired
                                          ? 'text-red-500'
                                          : isUrgent
                                            ? 'text-amber-500'
                                            : 'text-indigo-500'
                                      } />
                                    </div>
                                    <div className="flex-1 min-w-2 space-y-1" >
                                      <p className="text-xs font-bold text-slate-800 truncate">{doc.docname}</p>

                                      <p className="text-[10px] text-slate-500">
                                        Type: <span className="font-semibold text-slate-600 uppercase">{doc.doctype}</span>
                                        {doc.account && <> · Account: <span className="font-semibold text-slate-600">{doc.account}</span></>}
                                      </p>

                                      {doc.Description && (
                                        <p className="text-[10px] text-slate-500 truncate mt-0.5">
                                          Desc: <span className="font-bold text-slate-600">{doc.Description}</span>
                                        </p>
                                      )}
                                    </div>
                                    <div className="text-right shrink-0 space-y-1">
                                      <p className={`text-xs font-semibold ${isExpired ? 'text-red-600' : isUrgent ? 'text-amber-600' : 'text-emerald-600'
                                        }`}>
                                        {isExpired ? `Expired ${Math.abs(daysLeft)}d ago` : `${daysLeft}d left`}
                                      </p>
                                      <p className="text-[10px] text-slate-700">
                                        Exp: <span className="font-bold text-slate-600">

                                            {dayjs(expDate).format("DD-MMM-YYYY")}
                                        </span>
                                      </p>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                              <CheckCircle size={32} className="mb-2 text-emerald-400" />
                              <p className="text-xs">No expiring documents found</p>
                            </div>
                          )}
                        </div>

                      </div>
                    ) : (
                      <div className="h-full flex items-center justify-center text-[9px] text-slate-400">No docs</div>
                    )}
                  </div>
                </div>
              </div>
            </FadeInCard>
          </div>








        </main>
      </div>

      {/* Cash Balance Modal */}
      {showCashModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowCashModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[80vh] flex flex-col animate-fadeInUp">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div>
                <h3 className="text-sm font-bold text-slate-900 uppercase tracking-widest flex items-center gap-2">
                  <span className="w-1.5 h-5 bg-gradient-to-b from-emerald-500 to-green-500 rounded-full" />
                  Cash Balance Details
                </h3>
                <p className="text-xs text-slate-400 mt-0.5">Account-wise cash breakdown</p>
              </div>
              <button
                onClick={() => setShowCashModal(false)}
                className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <X size={16} className="text-slate-500" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {cashAccountLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 size={24} className="text-emerald-500 animate-spin" />
                  <span className="text-xs text-slate-400 ml-2">Loading cash data...</span>
                </div>
              ) : cashAccountData && cashAccountData.length > 0 ? (
                <div className="space-y-4">
                  {/* Total Cash Balance */}
                  <div className="text-center p-4 bg-gradient-to-r from-emerald-50 to-green-50 rounded-xl">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Total Cash Balance</p>
                    <p className="text-2xl font-extrabold text-slate-900 mt-1">{cashBranchMetrics?.CashBalance?.toLocaleString()}</p>
                  </div>

                  {/* Account Table */}
                  <div className="overflow-hidden rounded-xl border border-slate-200">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-200">
                          <th className="px-4 py-2 text-left text-[9px] font-bold text-slate-500 uppercase tracking-wider">Account Name</th>
                          <th
                            className="px-4 py-2 text-right text-[9px] font-bold text-slate-500 uppercase tracking-wider cursor-pointer select-none hover:text-emerald-600 transition-colors"
                            onClick={() => setCashSortOrder(prev => prev === null ? 'asc' : prev === 'asc' ? 'desc' : null)}
                          >
                            <span className="inline-flex items-center justify-end gap-1">
                              Balance
                              {cashSortOrder === 'asc' && <ArrowUp size={10} className="text-emerald-500" />}
                              {cashSortOrder === 'desc' && <ArrowDown size={10} className="text-emerald-500" />}
                            </span>
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {sortedCashAccountData.map((account, index) => (
                          <tr key={index} className="hover:bg-slate-50 transition-colors">
                            <td className="px-4 py-3">
                              <p className="text-xs font-semibold text-slate-800 truncate">{account.AccountName}</p>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <span className={`text-xs font-bold ${account.CashBalance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                {account.CashBalance?.toLocaleString()}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                  <Wallet size={32} className="mb-2 text-slate-300" />
                  <p className="text-xs">No cash account data available</p>
                </div>
              )}
            </div>

            <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 rounded-b-2xl">
              <button
                onClick={() => setShowCashModal(false)}
                className="w-full py-2 bg-gradient-to-r from-emerald-500 to-green-500 text-white rounded-xl text-sm font-semibold hover:shadow-lg transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bank Balance Modal */}
      {showBankModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowBankModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[80vh] flex flex-col animate-fadeInUp">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div>
                <h3 className="text-sm font-bold text-slate-900 uppercase tracking-widest flex items-center gap-2">
                  <span className="w-1.5 h-5 bg-gradient-to-b from-cyan-500 to-blue-500 rounded-full" />
                  Bank Balance Details
                </h3>
                <p className="text-xs text-slate-400 mt-0.5">Account-wise bank breakdown</p>
              </div>
              <button
                onClick={() => setShowBankModal(false)}
                className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <X size={16} className="text-slate-500" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {bankAccountLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 size={24} className="text-cyan-500 animate-spin" />
                  <span className="text-xs text-slate-400 ml-2">Loading bank data...</span>
                </div>
              ) : bankAccountData && bankAccountData.length > 0 ? (
                <div className="space-y-4">
                  {/* Total Bank Balance */}
                  <div className="text-center p-4 bg-gradient-to-r from-cyan-50 to-blue-50 rounded-xl">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Total Bank Balance</p>
                    <p className="text-2xl font-extrabold text-slate-900 mt-1">{bankTotalMetrics?.TotalBankBalance?.toLocaleString()}</p>
                  </div>

                  {/* Account Table */}
                  <div className="overflow-hidden rounded-xl border border-slate-200">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-200">
                          <th className="px-4 py-2 text-left text-[9px] font-bold text-slate-500 uppercase tracking-wider">Account Name</th>
                          <th
                            className="px-4 py-2 text-right text-[9px] font-bold text-slate-500 uppercase tracking-wider cursor-pointer select-none hover:text-cyan-600 transition-colors"
                            onClick={() => setBankSortOrder(prev => prev === null ? 'asc' : prev === 'asc' ? 'desc' : null)}
                          >
                            <span className="inline-flex items-center justify-end gap-1">
                              Balance
                              {bankSortOrder === 'asc' && <ArrowUp size={10} className="text-cyan-500" />}
                              {bankSortOrder === 'desc' && <ArrowDown size={10} className="text-cyan-500" />}
                            </span>
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {sortedBankAccountData.map((account, index) => (
                          <tr key={index} className="hover:bg-slate-50 transition-colors">
                            <td className="px-4 py-3">
                              <p className="text-xs font-semibold text-slate-800 truncate">{account.AccountName}</p>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <span className={`text-xs font-bold ${account.BankBalance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                {account.BankBalance?.toLocaleString()}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                  <Landmark size={32} className="mb-2 text-slate-300" />
                  <p className="text-xs">No bank account data available</p>
                </div>
              )}
            </div>

            <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 rounded-b-2xl">
              <button
                onClick={() => setShowBankModal(false)}
                className="w-full py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl text-sm font-semibold hover:shadow-lg transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Receivables Modal */}
      {showReceivablesModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowReceivablesModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[80vh] flex flex-col animate-fadeInUp">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div>
                <h3 className="text-sm font-bold text-slate-900 uppercase tracking-widest flex items-center gap-2">
                  <span className="w-1.5 h-5 bg-gradient-to-b from-amber-500 to-orange-500 rounded-full" />
                  Receivables Ageing Report
                </h3>
                <p className="text-xs text-slate-400 mt-0.5">Customer-wise outstanding breakdown by age</p>
              </div>
              <button
                onClick={() => setShowReceivablesModal(false)}
                className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <X size={16} className="text-slate-500" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {ageingLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 size={24} className="text-amber-500 animate-spin" />
                  <span className="text-xs text-slate-400 ml-2">Loading ageing data...</span>
                </div>
              ) : ageingData && ageingData.length > 0 ? (
                <div className="space-y-4">
                  {/* Total Receivables Summary */}
                  <div className="text-center p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Total Outstanding</p>
                    <p className="text-2xl font-extrabold text-slate-900 mt-1">
                      {ageingData.reduce((sum, item) => sum + (item.Total_Amount || 0), 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>

                  {/* Ageing Table */}
                  <div className="overflow-hidden rounded-xl border border-slate-200">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="bg-slate-50 border-b border-slate-200">
                            <th className="px-3 py-2 text-left text-[9px] font-bold text-slate-500 uppercase tracking-wider">Customer</th>
                            <th className="px-3 py-2 text-right text-[9px] font-bold text-slate-500 uppercase tracking-wider">0-30 Days</th>
                            <th className="px-3 py-2 text-right text-[9px] font-bold text-slate-500 uppercase tracking-wider">31-60 Days</th>
                            <th className="px-3 py-2 text-right text-[9px] font-bold text-slate-500 uppercase tracking-wider">61-90 Days</th>
                            <th className="px-3 py-2 text-right text-[9px] font-bold text-slate-500 uppercase tracking-wider">91-120 Days</th>
                            <th className="px-3 py-2 text-right text-[9px] font-bold text-slate-500 uppercase tracking-wider">Above 120</th>
                            <th
                              className="px-3 py-2 text-right text-[9px] font-bold text-slate-500 uppercase tracking-wider cursor-pointer select-none hover:text-amber-600 transition-colors"
                              onClick={() => setReceivablesSortOrder(prev => prev === null ? 'asc' : prev === 'asc' ? 'desc' : null)}
                            >
                              <span className="inline-flex items-center justify-end gap-1">
                                Total
                                {receivablesSortOrder === 'asc' && <ArrowUp size={10} className="text-amber-500" />}
                                {receivablesSortOrder === 'desc' && <ArrowDown size={10} className="text-amber-500" />}
                              </span>
                            </th>
                            <th className="px-3 py-2 text-left text-[9px] font-bold text-slate-500 uppercase tracking-wider">Sales Person</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {sortedAgeingData.map((row, index) => (
                            <tr key={index} className="hover:bg-slate-50 transition-colors">
                              <td className="px-3 py-3">
                                <p className="text-[10px] font-semibold text-slate-800 truncate max-w-[150px]">{row.CustomerName}</p>
                              </td>
                              <td className="px-3 py-3 text-right">
                                <span className={`text-[10px] font-bold ${(row.Age_0_30 || 0) < 0 ? 'text-red-600' : 'text-slate-700'}`}>
                                  {(row.Age_0_30 || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                </span>
                              </td>
                              <td className="px-3 py-3 text-right">
                                <span className={`text-[10px] font-bold ${(row.Age_31_60 || 0) < 0 ? 'text-red-600' : 'text-slate-700'}`}>
                                  {(row.Age_31_60 || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                </span>
                              </td>
                              <td className="px-3 py-3 text-right">
                                <span className={`text-[10px] font-bold ${(row.Age_61_90 || 0) < 0 ? 'text-red-600' : 'text-amber-600'}`}>
                                  {(row.Age_61_90 || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                </span>
                              </td>
                              <td className="px-3 py-3 text-right">
                                <span className={`text-[10px] font-bold ${(row.Age_91_120 || 0) < 0 ? 'text-red-600' : 'text-orange-600'}`}>
                                  {(row.Age_91_120 || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                </span>
                              </td>
                              <td className="px-3 py-3 text-right">
                                <span className={`text-[10px] font-bold ${(row.Age_Above120 || 0) < 0 ? 'text-red-600' : 'text-red-600'}`}>
                                  {(row.Age_Above120 || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                </span>
                              </td>
                              <td className="px-3 py-3 text-right">
                                <span className={`text-[10px] font-bold ${(row.Total_Amount || 0) < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                                  {(row.Total_Amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                </span>
                              </td>
                              <td className="px-3 py-3">
                                <span className="text-[10px] font-medium text-slate-600">{row.Sales_Person}</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                  <Clock size={32} className="mb-2 text-slate-300" />
                  <p className="text-xs">No ageing data available</p>
                </div>
              )}
            </div>

            <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 rounded-b-2xl">
              <button
                onClick={() => setShowReceivablesModal(false)}
                className="w-full py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl text-sm font-semibold hover:shadow-lg transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Collection Breakdown Modal */}
      {showCollectionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowCollectionModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[80vh] flex flex-col animate-fadeInUp">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div>
                <h3 className="text-sm font-bold text-slate-900 uppercase tracking-widest flex items-center gap-2">
                  <span className="w-1.5 h-5 bg-gradient-to-b from-blue-500 to-indigo-500 rounded-full" />
                  Collection Breakdown
                </h3>
                <p className="text-xs text-slate-400 mt-0.5">Monthly collection details</p>
              </div>
              <button
                onClick={() => setShowCollectionModal(false)}
                className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <X size={16} className="text-slate-500" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {collectionLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 size={24} className="text-blue-500 animate-spin" />
                  <span className="text-xs text-slate-400 ml-2">Loading collection data...</span>
                </div>
              ) : collectionMetrics ? (
                <div className="space-y-6">
                  {/* Total Collection */}
                  <div className="text-center p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Total Month Collection</p>
                    <p className="text-2xl font-extrabold text-slate-900 mt-1">{collectionMetrics.MonthTotalCollection?.toLocaleString()}</p>
                  </div>

                  {/* Breakdown */}
                  <div className="space-y-3">
                    <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider">Month Breakdown</h4>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="p-3 bg-blue-50 rounded-xl text-center">
                        <p className="text-[9px] font-bold text-blue-600 uppercase">Cash</p>
                        <p className="text-sm font-extrabold text-slate-900 mt-1">{collectionMetrics.MonthCash?.toLocaleString()}</p>
                      </div>
                      <div className="p-3 bg-emerald-50 rounded-xl text-center">
                        <p className="text-[9px] font-bold text-emerald-600 uppercase">Bank Transfer</p>
                        <p className="text-sm font-extrabold text-slate-900 mt-1">{collectionMetrics.MonthBankTransfer?.toLocaleString()}</p>
                      </div>
                      <div className="p-3 bg-amber-50 rounded-xl text-center">
                        <p className="text-[9px] font-bold text-amber-600 uppercase">Cheque</p>
                        <p className="text-sm font-extrabold text-slate-900 mt-1">{collectionMetrics.MonthCheque?.toLocaleString()}</p>
                      </div>
                    </div>
                  </div>

                  {/* Today's Collection */}
                  <div className="space-y-3">
                    <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider">Today's Collection</h4>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="p-3 bg-blue-50 rounded-xl text-center">
                        <p className="text-[9px] font-bold text-blue-600 uppercase">Cash</p>
                        <p className="text-sm font-extrabold text-slate-900 mt-1">{collectionMetrics.TodayCash?.toLocaleString()}</p>
                      </div>
                      <div className="p-3 bg-emerald-50 rounded-xl text-center">
                        <p className="text-[9px] font-bold text-emerald-600 uppercase">Bank Transfer</p>
                        <p className="text-sm font-extrabold text-slate-900 mt-1">{collectionMetrics.TodayBankTransfer?.toLocaleString()}</p>
                      </div>
                      <div className="p-3 bg-amber-50 rounded-xl text-center">
                        <p className="text-[9px] font-bold text-amber-600 uppercase">Cheque</p>
                        <p className="text-sm font-extrabold text-slate-900 mt-1">{collectionMetrics.TodayCheque?.toLocaleString()}</p>
                      </div>
                    </div>
                  </div>

                  {/* Pie Chart */}
                  <div className="space-y-3">
                    <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider">Collection Distribution</h4>
                    <div className="flex justify-center p-4 bg-slate-50 rounded-xl">
                      <PieChartComponent
                        data={[
                          { label: 'Cash', value: collectionMetrics.MonthCash || 0 },
                          { label: 'Bank Transfer', value: collectionMetrics.MonthBankTransfer || 0 },
                          { label: 'Cheque', value: collectionMetrics.MonthCheque || 0 }
                        ]}
                        size={140}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                  <DollarSign size={32} className="mb-2 text-slate-300" />
                  <p className="text-xs">No collection data available</p>
                </div>
              )}
            </div>

            <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 rounded-b-2xl">
              <button
                onClick={() => setShowCollectionModal(false)}
                className="w-full py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl text-sm font-semibold hover:shadow-lg transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PDC Received Modal */}
      {showPdcReceivedModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowPdcReceivedModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[80vh] flex flex-col animate-fadeInUp">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div>
                <h3 className="text-sm font-bold text-slate-900 uppercase tracking-widest flex items-center gap-2">
                  <span className="w-1.5 h-5 bg-gradient-to-b from-indigo-500 to-violet-500 rounded-full" />
                  PDC Received List
                </h3>
                <p className="text-xs text-slate-400 mt-0.5">Pending post-dated cheques received</p>
              </div>
              <button
                onClick={() => setShowPdcReceivedModal(false)}
                className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <X size={16} className="text-slate-500" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {pdcReceivedListLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 size={24} className="text-indigo-500 animate-spin" />
                  <span className="text-xs text-slate-400 ml-2">Loading PDC data...</span>
                </div>
              ) : pdcReceivedListData && pdcReceivedListData.length > 0 ? (
                <div className="space-y-4">
                  {/* Total Summary */}
                  <div className="text-center p-4 bg-gradient-to-r from-indigo-50 to-violet-50 rounded-xl">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Total PDC Received</p>
                    <p className="text-2xl font-extrabold text-slate-900 mt-1">
                      {pdcReceivedListData.reduce((sum, item) => sum + (item.AMOUNT || 0), 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>

                  {/* PDC Table */}
                  <div className="overflow-hidden rounded-xl border border-slate-200">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="bg-slate-50 border-b border-slate-200">
                            <th className="px-3 py-2 text-left text-[9px] font-bold text-slate-500 uppercase tracking-wider">Cheque No</th>
                            <th className="px-3 py-2 text-left text-[9px] font-bold text-slate-500 uppercase tracking-wider">Customer</th>
                            <th className="px-3 py-2 text-left text-[9px] font-bold text-slate-500 uppercase tracking-wider">Bank</th>
                            <th className="px-3 py-2 text-left text-[9px] font-bold text-slate-500 uppercase tracking-wider">Branch</th>
                            <th className="px-3 py-2 text-left text-[9px] font-bold text-slate-500 uppercase tracking-wider">Cheque Date</th>
                            <th
                              className="px-3 py-2 text-right text-[9px] font-bold text-slate-500 uppercase tracking-wider cursor-pointer select-none hover:text-indigo-600 transition-colors"
                              onClick={() => setPdcReceivedSortOrder(prev => prev === null ? 'asc' : prev === 'asc' ? 'desc' : null)}
                            >
                              <span className="inline-flex items-center justify-end gap-1">
                                Amount
                                {pdcReceivedSortOrder === 'asc' && <ArrowUp size={10} className="text-indigo-500" />}
                                {pdcReceivedSortOrder === 'desc' && <ArrowDown size={10} className="text-indigo-500" />}
                              </span>
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {sortedPdcReceivedListData.map((row, index) => (
                            <tr key={index} className="hover:bg-slate-50 transition-colors">
                              <td className="px-3 py-3">
                                <p className="text-[10px] font-semibold text-slate-800">{row.CHQNO}</p>
                              </td>
                              <td className="px-3 py-3">
                                <p className="text-[10px] font-semibold text-slate-800 truncate max-w-[200px]">{row.CNAME}</p>
                              </td>
                              <td className="px-3 py-3">
                                <span className="text-[10px] font-medium text-slate-600">{row.BANK}</span>
                              </td>
                              <td className="px-3 py-3">
                                <span className="text-[10px] font-medium text-slate-600">{row.DEPTNO}</span>
                              </td>
                              <td className="px-3 py-3">
                                <span className="text-[10px] font-medium text-slate-600">
                                  {row.CHQDATE ? new Date(row.CHQDATE).toLocaleDateString() : '-'}
                                </span>
                              </td>
                              <td className="px-3 py-3 text-right">
                                <span className="text-[10px] font-bold text-indigo-600">
                                  {(row.AMOUNT || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                  <CheckCircle size={32} className="mb-2 text-slate-300" />
                  <p className="text-xs">No PDC received data available</p>
                </div>
              )}
            </div>

            <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 rounded-b-2xl">
              <button
                onClick={() => setShowPdcReceivedModal(false)}
                className="w-full py-2 bg-gradient-to-r from-indigo-500 to-violet-500 text-white rounded-xl text-sm font-semibold hover:shadow-lg transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PDC Issued Modal */}
      {showPdcIssuedModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowPdcIssuedModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[80vh] flex flex-col animate-fadeInUp">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div>
                <h3 className="text-sm font-bold text-slate-900 uppercase tracking-widest flex items-center gap-2">
                  <span className="w-1.5 h-5 bg-gradient-to-b from-rose-500 to-pink-500 rounded-full" />
                  PDC Issued List
                </h3>
                <p className="text-xs text-slate-400 mt-0.5">Pending post-dated cheques issued</p>
              </div>
              <button
                onClick={() => setShowPdcIssuedModal(false)}
                className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <X size={16} className="text-slate-500" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {pdcIssuedListLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 size={24} className="text-rose-500 animate-spin" />
                  <span className="text-xs text-slate-400 ml-2">Loading PDC data...</span>
                </div>
              ) : pdcIssuedListData && pdcIssuedListData.length > 0 ? (
                <div className="space-y-4">
                  {/* Total Summary */}
                  <div className="text-center p-4 bg-gradient-to-r from-rose-50 to-pink-50 rounded-xl">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Total PDC Issued</p>
                    <p className="text-2xl font-extrabold text-slate-900 mt-1">
                      {pdcIssuedListData.reduce((sum, item) => sum + (item.AMOUNT || 0), 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>

                  {/* PDC Table */}
                  <div className="overflow-hidden rounded-xl border border-slate-200">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="bg-slate-50 border-b border-slate-200">
                            <th className="px-3 py-2 text-left text-[9px] font-bold text-slate-500 uppercase tracking-wider">Cheque No</th>
                            <th className="px-3 py-2 text-left text-[9px] font-bold text-slate-500 uppercase tracking-wider">Customer</th>
                            <th className="px-3 py-2 text-left text-[9px] font-bold text-slate-500 uppercase tracking-wider">Bank</th>
                            <th className="px-3 py-2 text-left text-[9px] font-bold text-slate-500 uppercase tracking-wider">Branch</th>
                            <th className="px-3 py-2 text-left text-[9px] font-bold text-slate-500 uppercase tracking-wider">Cheque Date</th>
                            <th
                              className="px-3 py-2 text-right text-[9px] font-bold text-slate-500 uppercase tracking-wider cursor-pointer select-none hover:text-rose-600 transition-colors"
                              onClick={() => setPdcIssuedSortOrder(prev => prev === null ? 'asc' : prev === 'asc' ? 'desc' : null)}
                            >
                              <span className="inline-flex items-center gap-1">
                                Amount
                                {pdcIssuedSortOrder === 'asc' && <ArrowUp size={10} className="text-rose-500" />}
                                {pdcIssuedSortOrder === 'desc' && <ArrowDown size={10} className="text-rose-500" />}
                              </span>
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {sortedPdcIssuedListData.map((row, index) => (
                            <tr key={index} className="hover:bg-slate-50 transition-colors">
                              <td className="px-3 py-3">
                                <p className="text-[10px] font-semibold text-slate-800">{row.CHQNO}</p>
                              </td>
                              <td className="px-3 py-3">
                                <p className="text-[10px] font-semibold text-slate-800 truncate max-w-[200px]">{row.CNAME}</p>
                              </td>
                              <td className="px-3 py-3">
                                <span className="text-[10px] font-medium text-slate-600">{row.BANK}</span>
                              </td>
                              <td className="px-3 py-3">
                                <span className="text-[10px] font-medium text-slate-600">{row.DEPTNO}</span>
                              </td>
                              <td className="px-3 py-3">
                                <span className="text-[10px] font-medium text-slate-600">
                                  {row.CHQDATE ? new Date(row.CHQDATE).toLocaleDateString() : '-'}
                                </span>
                              </td>
                              <td className="px-3 py-3 text-right">
                                <span className="text-[10px] font-bold text-rose-600">
                                  {(row.AMOUNT || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                  <CheckCircle size={32} className="mb-2 text-slate-300" />
                  <p className="text-xs">No PDC issued data available</p>
                </div>
              )}
            </div>

            <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 rounded-b-2xl">
              <button
                onClick={() => setShowPdcIssuedModal(false)}
                className="w-full py-2 bg-gradient-to-r from-rose-500 to-pink-500 text-white rounded-xl text-sm font-semibold hover:shadow-lg transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}


    </div>
  );
}